import React, { useCallback, useRef } from 'react'
import { inject, observer } from 'mobx-react'
import EventTypeSelector from '../../EventTypeSelector'
import { toJS } from 'mobx'

const defaultSettings = {
  allCategories: true
}

function mergedEventTypeSelectorSettings(baseSettings, scopedSettings) {
  const safeScopedSettings = scopedSettings || defaultSettings

  const mergedEventCategories = baseSettings.eventCategories?.map(eventCategory => {
    const selected = safeScopedSettings.allCategories ||
      safeScopedSettings.eventCategoryIds?.includes(eventCategory.id)

    return {
      id: eventCategory.id,
      name: eventCategory.name,
      active: eventCategory.active,
      selected,
      packageGroups: eventCategory.packageGroups.map(packageGroup => {
        const selected = safeScopedSettings.allCategories ||
          safeScopedSettings.eventCategoryIds?.includes(eventCategory.id) ||
          safeScopedSettings.packageGroupIds?.includes(packageGroup.id)

        return {
          id: packageGroup.id,
          name: packageGroup.name,
          active: packageGroup.active,
          selected,
          eventTypes: packageGroup.eventTypes.map(eventType => {
            const selected = safeScopedSettings.allCategories ||
              safeScopedSettings.eventCategoryIds?.includes(eventCategory.id) ||
              safeScopedSettings.packageGroupIds?.includes(packageGroup.id) ||
              safeScopedSettings.eventTypeIds?.includes(eventType.id)

            return {
              id: eventType.id,
              name: eventType.name,
              active: eventType.active,
              recommended: eventType.recommended,
              selected,
            }
          })
        }
      })
    }
  }) || []

  const mergedAddOnCategories = baseSettings.addOnCategories?.map(addOnCategory => {
    const selected = safeScopedSettings.allAddOnCategories ||
      safeScopedSettings.addOnCategoryIds?.includes(addOnCategory.id)

    return {
      id: addOnCategory.id,
      name: addOnCategory.name,
      active: addOnCategory.active,
      selected,
      addOns: addOnCategory.addOns.map(addOn => {
        const selected = safeScopedSettings.allAddOnCategories ||
          safeScopedSettings.addOnCategoryIds?.includes(addOnCategory.id) ||
          safeScopedSettings.addOnIds?.includes(addOn.id)

        return {
          id: addOn.id,
          name: addOn.name,
          active: addOn.active,
          selected,
        }
      })
    }
  }) || []

  const mergedExtraCategories = baseSettings.extraCategories?.map(extraCategory => {
    const selected = safeScopedSettings.extraCategoryIds?.includes(extraCategory.id)

    return {
      id: extraCategory.id,
      name: extraCategory.name,
      pluralizedName: extraCategory.pluralizedName,
      pluralized_name: extraCategory.pluralized_name,
      active: extraCategory.active,
      selected,
      extraGroups: extraCategory.extraGroups.map(extraGroup => {
        const selected = safeScopedSettings.extraCategoryIds?.includes(extraCategory.id) ||
          safeScopedSettings.extraGroupIds?.includes(extraGroup.id)

        return {
          id: extraGroup.id,
          name: extraGroup.name,
          active: extraGroup.active,
          selected,
          extras: extraGroup.extras.map(extra => {
            const selected = safeScopedSettings.extraCategoryIds?.includes(extraCategory.id) ||
              safeScopedSettings.extraGroupIds?.includes(extraGroup.id) ||
              safeScopedSettings.extraIds?.includes(extra.id)

            return {
              id: extra.id,
              name: extra.name,
              active: extra.active,
              selected,
            }
          })
        }
      })
    }
  }) || []

  // Only include the properties we need, not the entire baseSettings
  // This prevents sharing references between the main selector and conditional selector
  return {
    allCategories: safeScopedSettings.allCategories,
    allAddOnCategories: safeScopedSettings.allAddOnCategories,
    eventCategories: mergedEventCategories,
    addOnCategories: mergedAddOnCategories,
    extraCategories: mergedExtraCategories
  }
}

const ConditionalFieldEventTypeSelector = observer(({ store, value, onChange }) => {
  const valueRef = useRef(JSON.stringify(value))

  const handleChange = useCallback(value => {
    const serializedValue = JSON.stringify(value)
    if(serializedValue === valueRef.current) return
    valueRef.current = serializedValue
    onChange(value)
  }, [ onChange ])

  const merged = mergedEventTypeSelectorSettings(store.eventTypeSelectorSettings, value)

  return (
    <EventTypeSelector
      {...merged}
      supportsPackages={true}
      supportsAddOns={true}
      supportsBackdrops={false}
      supportsMiniSessions={false}
      supportsExtras={true}
      onChange={handleChange}
    />
  )
})

export default inject("store")(ConditionalFieldEventTypeSelector)
